import type { ReactNode } from 'react'
import { Link } from 'react-router'
import type { InlineText } from './types'

/**
 * The Help centre's inline syntax (see types.ts). A copy of the Dev docs renderer with one
 * difference: `backticks` mark a label as it appears on screen, shown as a small chip rather
 * than as code.
 */
const TOKEN = /(`[^`]+`|\*\*[^*]+\*\*|\[[^\]]+\]\([^)\s]+\))/g

const linkClass =
  'font-medium text-teal underline decoration-teal/30 underline-offset-2 hover:decoration-teal'

function renderToken(token: string, key: number): ReactNode {
  if (token.startsWith('`')) {
    return (
      <span
        key={key}
        className="help-chip inline rounded-sm border border-line bg-wash px-1.5 py-px text-[0.92em] font-medium text-ink break-words"
      >
        {token.slice(1, -1)}
      </span>
    )
  }
  if (token.startsWith('**')) {
    return (
      <strong key={key} className="font-semibold text-ink">
        {token.slice(2, -2)}
      </strong>
    )
  }
  const match = /^\[([^\]]+)\]\(([^)\s]+)\)$/.exec(token)
  if (!match) return token
  const [, label, href] = match as unknown as [string, string, string]
  if (href.startsWith('/') || href.startsWith('#')) {
    return (
      <Link key={key} to={href} className={linkClass}>
        {label}
      </Link>
    )
  }
  return (
    <a key={key} href={href} className={linkClass} target="_blank" rel="noreferrer">
      {label}
    </a>
  )
}

export function Inline({ text }: { text: InlineText }) {
  const parts = text.split(TOKEN)
  return <>{parts.map((part, index) => (index % 2 === 1 ? renderToken(part, index) : part))}</>
}

/** Plain text of an inline string, for search. */
export function plainText(text: InlineText): string {
  return text
    .replace(/\[([^\]]+)\]\([^)\s]+\)/g, '$1')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/`([^`]+)`/g, '$1')
}
